

import emoji from "react-easy-emoji";
import splashAnimation from "@assets/lottie/splashAnimation"; // Rename to your file name for custom animation

// CRA/webpack 5 quirk: require("x.png") returns the URL string, but
// require("x.svg") returns a MODULE ({default: url, ReactComponent}) because
// of the SVGR loader chain. Passing that object as an <img src> renders
// "[object Object]" and breaks the section. This unwraps both shapes.
const asset = m => (m && m.default) || m;

// Splash Screen

const splashScreen = {
  enabled: true, // set false to disable splash screen
  animation: splashAnimation,
  duration: 2000 // Set animation duration as per your animation
};

// Summary And Greeting Section

const illustration = {
  animated: true // Set to false to use static SVG
};

const greeting = {
  username: "Rashid Ahmad",
  title: "I'm Rashid",
  subTitle: emoji(
    "Salesforce Technical Lead 🚀 with 8 years of experience architecting scalable, multi-cloud enterprise solutions. I design secure, cost-effective Salesforce + AI platforms — Agentforce, Sierra AI, and custom Einstein models — that automate work and transform customer engagement."
  ),
  // No resumeLink field on purpose — the "Download my resume" button
  // (Greeting.js) always goes to /resume?download=1, which renders the
  // live, admin-managed resume and generates the PDF client-side at click
  // time (see Resume.js's downloadPdf()). There is no static PDF file to
  // link to; a resumeLink field here would be misleading dead data.
  displayGreeting: true // Set false to hide this section, defaults to true
};

// Impact strip — architect-level proof points shown right under the hero.
// Hardcoded on purpose: this must render even with every backend down.
const impactStats = {
  display: true,
  stats: [
    {value: "8+", label: "Years on the Salesforce platform"},
    {value: "12", label: "Salesforce & Copado certifications"},
    {value: "90%", label: "AHT cut via AI document processing"},
    {value: "38", label: "Countries served by one costing engine"}
  ]
};

// Blog hero — the animated "AI × Salesforce" banner at the top of /blog.
// Fully admin-editable (Admin > Portfolio > Blog hero) so none of this text
// is hardcoded on the live site; this object is only the fallback used
// before the DB loads (or if the API is down). Content is drawn from real
// work: Agentforce, Sierra AI (email, voice & chat), Einstein Case
// Classification, EAAA, MuleSoft-integrated IDP, Prompt Builder, CRM
// Analytics — matching the resume/portfolio elsewhere on the site, not
// generic AI buzzwords. Note: Sierra AI work spans all three channels, not
// just voice — don't shorten it to "Sierra Voice AI" anywhere. IDP
// (Intelligent Document Processing) and MuleSoft are two distinct things —
// MuleSoft is the integration layer that orchestrates the IDP pipeline, not
// the document-processing engine itself — so they're named separately.
const blogHeroContent = {
  display: true,
  eyebrow: "AI × Salesforce Engineering",
  titlePrefix: "Building",
  titleGradient: "AI that works inside Salesforce",
  taglines: [
    "Auto-resolving Salesforce cases with LLM-powered Apex.",
    "Agentforce & Einstein agents that triage, draft, and close cases.",
    "Sierra AI across email, voice, and chatbot — one AI, every channel.",
    "MuleSoft orchestrating an IDP pipeline that turns attachments into structured data.",
    "LWC copilots with visual confidence scoring for human review.",
    "Prompt Builder frameworks admins can tune without a deployment."
  ],
  chips: [
    "Agentforce", "Einstein Case Classification", "Sierra AI",
    "IDP via MuleSoft", "Prompt Builder", "CRM Analytics", "Apex + LLM",
    "EAAA Gen AI", "Auto-Triage", "RAG on CRM Data"
  ],
  pipeline: [
    {icon: "fas fa-inbox", label: "Case In"},
    {icon: "fas fa-brain", label: "AI Analyze"},
    {icon: "fas fa-magic", label: "Draft Reply"},
    {icon: "fas fa-chart-line", label: "Confidence Score"},
    {icon: "fas fa-check-circle", label: "Auto-Resolve"}
  ]
};

// Core Salesforce Expertise — the platform depth behind the AI headlines.
// Sourced directly from 8 years across EXL/Infosys/Fujitsu: sharing &
// security model, governor-limit-safe Apex, LWC/Aura UI, and integration.
const coreExpertise = {
  display: true,
  title: "Deep Core Salesforce",
  subTitle: "THE PLATFORM FOUNDATION BEHIND EVERY AI FEATURE I SHIP",
  areas: [
    {
      icon: "fas fa-shield-alt",
      title: "Data Model & Security",
      items: [
        "Custom Objects, Fields & Apex Managed Sharing",
        "Sharing & Visibility architecture (Platform Sharing and Visibility Architect certified)",
        "SOQL / SOSL and governor-limit-safe query design"
      ]
    },
    {
      icon: "fas fa-cogs",
      title: "Automation & Logic",
      items: [
        "Apex Triggers, Batch, Future & Queueable classes",
        "Flows, Process Builder & approval processes",
        "Reusable Error Log / Exception frameworks (Splunk, New Relic)"
      ]
    },
    {
      icon: "fas fa-bolt",
      title: "UI Layer",
      items: [
        "Lightning Web Components (LWC ES6+) & Aura Components",
        "Visualforce for legacy extension points",
        "Lightning Design System migrations from Classic"
      ]
    },
    {
      icon: "fas fa-network-wired",
      title: "Integration",
      items: [
        "REST / SOAP web services, webhooks & Connected Apps",
        "MuleSoft-orchestrated Intelligent Document Processing (IDP) pipelines",
        "CI/CD delivery with Salesforce DX, Copado & GitHub Actions"
      ]
    }
  ]
};

// Social Media Links

const socialMediaLinks = {
  github: "https://github.com/rashidahmad03",
  linkedin: "https://www.linkedin.com/in/rashidahmad03/",
  gmail: "sfdchero007@gmail.com",
  gitlab: "https://gitlab.com/rashidahmad03",
  youtube: "https://www.youtube.com/@rashidahmad5476",
  medium: "https://medium.com/@rashidahmad2135",
  stackoverflow: "",
  whatsapp:"https://wa.me/919643022135",
  display: true //
};

// Skills Section

const skillsSection = {
  title: "What I do",
  subTitle: "SALESFORCE ARCHITECT-TRACK LEAD — CORE PLATFORM, AGENTIC AI & INTEGRATION",
  skills: [
    emoji(
      "⚡ Architect multi-cloud Salesforce solutions across Sales, Service, Experience Cloud and FSL — governor-safe Apex, LWC, and secure sharing models"
    ),
    emoji(
      "⚡ Deliver Agentic AI on-platform: Agentforce, Sierra AI across email/voice/chat, Einstein Case Classification, Prompt Builder and CRM Analytics that measurably cut handle time"
    ),
    emoji(
      "⚡ Integrate the enterprise: MuleSoft-orchestrated IDP, Five9/Twilio CTI, AWS, SAP and REST/webhook pipelines with real-time data synchronization"
    )
  ],

  /* Make Sure to include correct Font Awesome Classname to view your icon
https://fontawesome.com/icons?d=gallery */

  softwareSkills: [
    {
      skillName: "Salesforce",
      fontAwesomeClassname: "fab fa-salesforce"
    },
    {
      skillName: "Agentforce",
      fontAwesomeClassname: "fas fa-robot"
    },
    {
      skillName: "Einstein AI",
      fontAwesomeClassname: "fas fa-brain"
    },
    {
      skillName: "CRM Analytics",
      fontAwesomeClassname: "fas fa-chart-line"
    },
    {
      skillName: "HTML",
      fontAwesomeClassname: "fab fa-html5"
    },
    {
      skillName: "CSS",
      fontAwesomeClassname: "fab fa-css3-alt"
    },
   
    {
      skillName: "JavaScript",
      fontAwesomeClassname: "fab fa-js"
    },
    {
      skillName: "ReactJS",
      fontAwesomeClassname: "fab fa-react"
    },
    {
      skillName: "NodeJs",
      fontAwesomeClassname: "fab fa-node"
    },
    {
      skillName: "APEX",
      fontAwesomeClassname: "fas fa-user-cog"
    },
    {
      skillName: "JAVA",
      fontAwesomeClassname: "fab fa-java"
    },
    {
      skillName: "SQL",
      fontAwesomeClassname: "fas fa-database"
    },
    {
      skillName: "AWS",
      fontAwesomeClassname: "fab fa-aws"
    },
    {
      skillName: "AURA",
      fontAwesomeClassname: "fas fa-bolt"
    },
    {
      skillName: "Lightning Web Components(LWC)",
      fontAwesomeClassname:"fas fa-poo-storm"
    },
    {
      skillName: "Visualforce",
      fontAwesomeClassname: "fas fa-code"
    }
  ],
  display: true // Set false to hide this section, defaults to true
};

// Education Section

const educationInfo = {
  display: true, // Set false to hide this section, defaults to true
  schools: [
    {
      schoolName: "AKTU",
      logo: asset(require("@assets/images/aktu.png")),
      subHeader: "Bachelor of Science in Computer Science",
      duration: "August 2014 - June 2018"
    }
  ]
};

// Your top 3 proficient stacks/tech experience

const techStack = {
  viewSkillBars: true, //Set it to true to show Proficiency Section
  experience: [
    {
      Stack: "Salesforce Architecture (Apex · LWC · Sharing)",
      progressPercentage: "90%"
    },
    {
      Stack: "AI on Salesforce (Agentforce · Einstein · Prompt)",
      progressPercentage: "85%"
    },
    {
      Stack: "Web & Integration (React · REST · MuleSoft)",
      progressPercentage: "80%"
    }
  ],
  displayCodersrank: true
};
const bigProjects = {
  title: "Big Projects",
  subtitle: "",
  projects: [
    {
      image: asset(require("@assets/images/sfadminagent.svg")),
      projectName: "SF Admin Agent — AI Virtual Salesforce Administrator",
      projectDesc: "An AI-powered assistant driven by the Claude API that acts as a virtual system administrator: it automates user provisioning, permission management, data cleaning and instant report generation through a simple chat or email interface, while proactively monitoring license usage, storage, API consumption and audit trails to flag high-risk security changes across Production and Sandbox.",
      footerLink: [
        {
          name: "GitHub Profile",
          url: "https://github.com/rashidahmad03"
        }
      ]
    },
    {
      image: asset(require("@assets/images/agentforcecc.svg")),
      projectName: "Agentforce for Experience Cloud",
      projectDesc: "An Experience Cloud solution that lets portal users independently create accurate quotes with Agentforce providing real-time product information and intelligent agent support — reducing lead follow-up effort and speeding up the quoting process end to end.",
      footerLink: [
        {
          name: "GitHub Profile",
          url: "https://github.com/rashidahmad03"
        }
      ]
    },
    {
      image: asset(require("@assets/images/osp.svg")),
      projectName: "Onboarding Services Portal (OSP)",
      projectDesc: "A secure Experience Cloud application fully automating customer onboarding by eliminating manual underwriting. An AI-driven Intelligent Document Processing engine instantly parses and validates sensitive US documents, feeding a custom Apex and Flow-based rules engine — delivering zero-touch auto-approvals for loan products in under five minutes while ensuring strict US financial compliance.",
      footerLink: [
        {
          name: "GitHub Profile",
          url: "https://github.com/rashidahmad03"
        }
      ]
    },
    {
      image: asset(require("@assets/images/chatgpt.png")),
      projectName: "Integration of GPT Gemini (Gen AI) with Salesforce",
      projectDesc: "Integrating Chat GPT (Gemini) into Salesforce significantly enhances CRM capabilities. By leveraging advanced generative AI features, organizations can improve user interactions and automate responses. This integration streamlines processes and increases efficiency. Overall, it fosters better customer relationship management. Adopting Gemini can transform how businesses engage with their customers.",
      footerLink: [
        {
          name: "Youtube Link",
          url: "https://youtu.be/695YUcnu_8M?si=lE7-bAmboHNyK6Ly"
        },
        {
          name: "GitHub Link",
          url: "https://github.com/rashidahmad03/Gen_AI_with_Salesforce"
        }
        //  you can add extra buttons here.
      ]
    },
    {
      image: asset(require("@assets/images/cicd.png")),
      projectName: "CICD-Salesforce",
      projectDesc: "A CI/CD system automates the deployment of changes from sandbox to production, facilitating developer collaboration and maintaining a detailed history of deployments, which is crucial for Salesforce developers and architects to understand in order to effectively utilize tools like GitHub Actions and select the right DevOps solutions.",
      footerLink: [
        {
          name: "Youtube Link",
          url: "https://youtu.be/rRDTVsfZYhY?si=_jnxz-2w-ok-inxb"
        },
        {
          name: "GitHub Link",
          url: "https://github.com/rashidahmad03/CICD-Salesforce/"
        }
        //  you can add extra buttons here.
      ]
    }
    ,  {
      image: asset(require("@assets/images/stos.jpg")),
      projectName: "Salesforce To Salesforce",
      projectDesc: "Salesforce to Salesforce simplifies record sharing and data updates between businesses using Salesforce, allowing for easy integration of lead and opportunity data, management of sharing activities, and streamlined business processes through workflow and assignment rules",
      footerLink: [
        {
          name: "Youtube Link",
          url: "https://youtu.be/VtziD6lXHLE?si=dUF2UaHj9BV-6gj1"
        }
        //  you can add extra buttons here.
      ]
    },
    {
      image: asset(require("@assets/images/googleassistant.png")),
      projectName: "Google Assistant integration with Salesforce",
      projectDesc: "Integrating Google Assistant with Salesforce through REST web services involves setting up a webhook fulfillment that allows seamless communication between the two platforms. This integration enhances user interactions by enabling voice commands to trigger actions within Salesforce.",
      footerLink: [
        {
          name: "Youtube Link",
          url: "https://youtu.be/Qxj2dt_FLUI?si=YtkRK8uX586hiVwL"
        }
        //  you can add extra buttons here.
      ]
    }
  ],
  display: true // Set false to hide this section, defaults to true
};

// Work experience section

const workExperiences = {
  display: true, //Set it to true to show workExperiences Section
  experience: [
    {
      role: "Technical Lead",
      company: "EXL",
      companylogo: asset(require("@assets/images/exl.svg")),
      date: "June 2025 – Present",
      desc: "Leading cross-functional developer teams for the McKesson healthcare account — from stakeholder requirements to enterprise architecture and delivery.",
      descBullets: [
        "Delivered Sierra AI across email, voice, and chatbot channels — including a CTI architecture for the voice channel connecting Sierra AI with Five9 and Service Cloud, streaming real-time conversation context via Twilio WebSockets into Salesforce.",
        "Architected an AI case intake pipeline with Einstein Case Classification and Email Agent Assist Generative AI, cutting Average Handle Time by 90% with an Intelligent Document Processing (IDP) pipeline orchestrated through MuleSoft.",
        "Built a metadata-driven Generative AI prompt framework — admins tune AI behavior with zero code deployments — and led the MIAW omnichannel chat migration."
      ]
    },
    {
      role: "Consultant → Senior Associate → Associate",
      company: "Infosys",
      companylogo: asset(require("@assets/images/infosys.svg")),
      date: "Apr 2021 – June 2025",
      desc: "End-to-end Salesforce solution design and delivery for Syngenta, EFSA, and Telstra — with technical leadership and mentoring of junior developers.",
      descBullets: [
        "Designed a global financial cost-calculation engine spanning 38 countries with LWC, complex Apex, and Custom Metadata Types.",
        "Delivered a 3-month real-time webhook integration between Salesforce and Science Exchange; integrated Gemini Gen AI for quote summarization.",
        "Built Telstra NBN plan journeys (4G/5G/Wireless), Siebel-to-CPQ order automation, and a reusable exception framework wired to Splunk and New Relic."
      ]
    },
    {
      role: "Assistant Application Developer",
      company: "Fujitsu",
      companylogo: asset(require("@assets/images/fujitsu.svg")),
      date: "Oct 2018 – Mar 2021",
      desc: "Salesforce delivery for AIG and Moody's Analytics — insurance deal management and Lightning modernization.",
      descBullets: [
        "Built the A&H Partnership deal module and Cases module for AIG with RESTful APIs, batch Apex, and a deal-history analytics data model.",
        "Migrated Moody's Analytics from Classic to Lightning; developed Aura inventory components and approval automation."
      ]
    }
  ]
};

// Resume Section
const resumeSection = {
  title: "Resume",
  subtitle: "Feel free to download my resume",

  display: true 
};

const contactInfo = {
  title: emoji("Contact Me ☎️"),
  subtitle:
    "Want to chat about a project or just say hello? All are welcome to my inbox.",
  number: "+91-9643022135",
  email_address: "sfdchero007@gmail.com"
};



const isHireable = false;

export {
  illustration,
  greeting,
  impactStats,
  coreExpertise,
  blogHeroContent,
  socialMediaLinks,
  splashScreen,
  skillsSection,
  educationInfo,
  techStack,
  workExperiences,
  bigProjects,
  contactInfo,
  isHireable,
  resumeSection
};
